import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  newBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '10px 20px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
  yearLabel: {
    fontSize: '13px', fontWeight: '700', color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '1px',
    marginBottom: '8px', marginTop: '24px',
  },
  groupHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 16px', backgroundColor: '#fff',
    borderRadius: '8px', border: '1px solid #e5e7eb',
    cursor: 'pointer', marginBottom: '8px', userSelect: 'none',
  },
  groupHeaderOpen: { backgroundColor: '#EBF5FF', border: '1px solid #bfdbfe' },
  groupTitle: { fontSize: '14px', fontWeight: '700', color: '#111827', flex: 1 },
  groupCount: {
    fontSize: '11px', fontWeight: '700', color: '#0052CC',
    backgroundColor: '#dbeafe', padding: '2px 8px', borderRadius: '20px',
  },
  groupChevron: { fontSize: '12px', color: '#6b7280' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '0',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '8px', overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px',
    borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb',
  },
  td: {
    padding: '12px 16px', fontSize: '13px', color: '#374151',
    borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle',
  },
  scenarioTitle: { fontWeight: '600', color: '#111827', marginBottom: '2px' },
  scenarioMeta: { fontSize: '11px', color: '#9ca3af' },
  statusBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
    fontSize: '11px', fontWeight: '700',
  },
  scoreBadge: {
    display: 'inline-block', padding: '2px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: '700',
  },
  actions: { display: 'flex', gap: '8px' },
  viewBtn: {
    padding: '5px 12px', backgroundColor: '#f3f4f6', color: '#374151',
    border: 'none', borderRadius: '6px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer',
  },
  rerunBtn: {
    padding: '5px 12px', backgroundColor: '#EBF5FF', color: '#0052CC',
    border: 'none', borderRadius: '6px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer',
  },
  progressBar: {
    width: '60px', height: '4px', backgroundColor: '#e5e7eb',
    borderRadius: '2px', overflow: 'hidden', display: 'inline-block',
    verticalAlign: 'middle', marginLeft: '8px',
  },
  progressFill: { height: '100%', backgroundColor: '#0052CC', borderRadius: '2px' },
  empty: {
    textAlign: 'center', padding: '64px 32px',
    backgroundColor: '#fff', borderRadius: '12px',
    border: '1px solid #e5e7eb',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: '#6b7280', marginBottom: '24px' },
  loading: { textAlign: 'center', padding: '64px', color: '#6b7280' },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '16px',
  },
};

const STATUS_COLORS = {
  completed: { backgroundColor: '#dcfce7', color: '#16a34a' },
  stopped: { backgroundColor: '#fef3c7', color: '#d97706' },
  failed: { backgroundColor: '#fef2f2', color: '#dc2626' },
  running: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
};

function getScoreColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#0052CC';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

function groupByMonthYear(items) {
  const groups = {};
  items.forEach(item => {
    const date = new Date(item.created_at);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${month}`;
    if (!groups[key]) groups[key] = { year, month, items: [] };
    groups[key].items.push(item);
  });
  return Object.values(groups).sort((a, b) =>
    b.year !== a.year ? b.year - a.year : b.month - a.month
  );
}

export default function SimulationList() {
  const { currentUser, isPro } = useAuth();
  const navigate = useNavigate();

  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => { fetchSimulations(); }, []);
  
  async function fetchSimulations() {
    try {
      setLoading(true);
      const historyDays = isPro ? null : 7;
      const url = historyDays
        ? `${API}/api/analytics/history?owner_id=${currentUser.uid}&days=${historyDays}`
        : `${API}/api/analytics/history?owner_id=${currentUser.uid}`;
      const res = await fetch(url);
      const data = await res.json();
      const sims = Array.isArray(data) ? data : [];
      setSimulations(sims);

      if (sims.length > 0) {
        const groups = groupByMonthYear(sims);
        if (groups.length > 0) {
          const first = groups[0];
          setOpenGroups({ [`${first.year}-${first.month}`]: true });
        }
      }
    } catch {
      setError('Failed to load simulations.');
    } finally {
      setLoading(false);
    }
  }

  function toggleGroup(key) {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const groups = groupByMonthYear(simulations);
  const byYear = {};
  groups.forEach(g => {
    if (!byYear[g.year]) byYear[g.year] = [];
    byYear[g.year].push(g);
  });
  const sortedYears = Object.keys(byYear).sort((a, b) => b - a);

  if (loading) return <AppLayout><div style={styles.loading}>Loading simulations...</div></AppLayout>;

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Simulations</div>
          <div style={styles.sub}>
            {!isPro && (
              <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                · Showing last 7 days — <a href="mailto:info@converstation.ai" style={{ color: '#0052CC' }}>Upgrade to Professional</a> for full history
              </span>
            )}
            {simulations.length} simulation{simulations.length !== 1 ? 's' : ''} · grouped by date
          </div>
        </div>
        <button style={styles.newBtn} onClick={() => navigate('/scenarios')}>
          + New Simulation
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {simulations.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>▶</div>
          <div style={styles.emptyTitle}>No simulations yet</div>
          <div style={styles.emptyText}>
            Go to Scenarios and click ▶ Run to start your first simulation.
          </div>
          <button style={styles.newBtn} onClick={() => navigate('/scenarios')}>
            Go to Scenarios →
          </button>
        </div>
      ) : (
        sortedYears.map(year => (
          <div key={year}>
            <div style={styles.yearLabel}>{year}</div>
            {byYear[year].map(group => {
              const key = `${group.year}-${group.month}`;
              const isOpen = !!openGroups[key];
              return (
                <div key={key}>
                  <div
                    style={{ ...styles.groupHeader, ...(isOpen ? styles.groupHeaderOpen : {}) }}
                    onClick={() => toggleGroup(key)}
                  >
                    <div style={styles.groupTitle}>{MONTHS[group.month]}</div>
                    <div style={styles.groupCount}>
                      {group.items.length} simulation{group.items.length !== 1 ? 's' : ''}
                    </div>
                    <div style={styles.groupChevron}>{isOpen ? '▲' : '▼'}</div>
                  </div>
                  {isOpen && (
                    <div style={styles.card}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>Scenario</th>
                            <th style={styles.th}>Provider · Model</th>
                            <th style={styles.th}>Turns</th>
                            <th style={styles.th}>Score</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Date</th>
                            <th style={styles.th}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map(sim => {
                            const progress = sim.max_turns > 0
                              ? Math.round((sim.current_turn / sim.max_turns) * 100)
                              : 0;
                            return (
                              <tr key={sim.id}>
                                <td style={styles.td}>
                                  <div style={styles.scenarioTitle}>{sim.scenario_title}</div>
                                </td>
                                <td style={styles.td}>
                                  <div>{sim.llm_provider}</div>
                                  <div style={styles.scenarioMeta}>{sim.llm_model}</div>
                                </td>
                                <td style={styles.td}>
                                  <span>{sim.current_turn}/{sim.max_turns}</span>
                                  <div style={styles.progressBar}>
                                    <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                                  </div>
                                </td>
                                <td style={styles.td}>
                                  {sim.overall_score !== null ? (
                                    <span style={{
                                      ...styles.scoreBadge,
                                      backgroundColor: `${getScoreColor(sim.overall_score)}20`,
                                      color: getScoreColor(sim.overall_score),
                                    }}>
                                      {sim.overall_score}/100
                                    </span>
                                  ) : '—'}
                                </td>
                                <td style={styles.td}>
                                  <span style={{
                                    ...styles.statusBadge,
                                    ...(STATUS_COLORS[sim.status] || STATUS_COLORS.completed),
                                  }}>
                                    {sim.status}
                                  </span>
                                </td>
                                <td style={styles.td}>
                                  {new Date(sim.created_at).toLocaleDateString()}
                                </td>
                                <td style={styles.td}>
                                  <div style={styles.actions}>
                                    <button
                                      style={styles.viewBtn}
                                      onClick={() => navigate(`/results/${sim.id}`)}
                                    >
                                      Results
                                    </button>
                                    <button
                                      style={styles.rerunBtn}
                                      onClick={() => navigate(`/simulations/new?scenario=${sim.scenario_id}`)}
                                    >
                                      ↺ Rerun
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </AppLayout>
  );
}
